<template>
    <div v-if="initDone" class="schedule-viewer">
        <div id="schedule-category-search" v-if="totalMats > 0" class="mb-3 scheduled-category-search">
            <QueryInput
                v-model="scheduledCategorySearchQuery"
                :debounce-ms="0"
                :is-expanded="true"
                :placeholder="translate('admin_schedule_manage.scheduled_category_search')"
                :no-margin="true"
                :size="'is-small'"
            />
        </div>

        <div class="columns mat-schedules-container" :class="{ 'is-multiline': multiRowMode }">
            <DropZone @category-scheduled="onCategoryScheduled" @category-unscheduled="onCategoryUnscheduled" />

            <SingleMatSchedule :mat-schedule="matSchedule" v-for="matSchedule in matSchedules" :key="matSchedule.id" />
        </div>

        <ScheduleSelectionActionBar />
    </div>
    <div v-else>
        <div>
            <h4>{{ translate("admin_schedule_manage.loading_mats") }}</h4>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed, nextTick, onMounted } from "vue";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import type HttpError from "../../Api/Utils/HttpError";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import SingleMatSchedule from "./SingleMatSchedule.vue";
    import DropZone from "./DropZone.vue";
    import ScheduleSelectionActionBar from "./ScheduleSelectionActionBar.vue";
    import QueryInput from "../../Common/QueryInput.vue";
    import { useFeatureAnnouncement } from "../../Common/FeatureAnnouncements/useFeatureAnnouncement";

    // Stores
    const showScheduleStore = useShowScheduleStore();

    const props = defineProps<{
        currentUserId: number;
    }>();

    const emit = defineEmits<{ scheduleGone: [] }>();

    // State
    const initDone = ref(false);
    // Computed
    const matSchedules = computed(() => showScheduleStore.matSchedules);
    const multiRowMode = computed(() => showScheduleStore.multiRowMode);
    const totalMats = computed(() => Object.keys(matSchedules.value).length);
    const scheduledCategorySearchQuery = computed({
        get() {
            return showScheduleStore.scheduledCategorySearchQuery;
        },
        set(value: string) {
            showScheduleStore.scheduledCategorySearchQuery = value;
        },
    });

    // Methods
    function handleFetchError(err: HttpError) {
        if (err.statusCode === 404) {
            emit("scheduleGone");
            return;
        }
        handleError(err);
    }

    function fetchMatSchedules(): Promise<void> {
        return showScheduleStore.refreshAllMatSchedules();
    }

    function fetchSingleMatSchedule(matIds: number[]): Promise<void> {
        return showScheduleStore.refreshSingleMatSchedule(matIds);
    }

    /**
     * Called when a category is scheduled from DropZone to a mat.
     * Refreshes the affected mats.
     */
    function onCategoryScheduled(matIds: number[]) {
        fetchSingleMatSchedule(matIds).catch(handleFetchError);
    }

    /**
     * Called when a category is unscheduled from a mat to DropZone.
     * Refreshes the affected mats.
     */
    function onCategoryUnscheduled(matIds: number[]) {
        fetchSingleMatSchedule(matIds).catch(handleFetchError);
    }

    // Lifecycle
    onMounted(() => {
        fetchMatSchedules()
            .catch(handleFetchError)
            .finally(() => {
                initDone.value = true;

                if (props.currentUserId && totalMats.value > 0) {
                    nextTick(() => {
                        useFeatureAnnouncement().showEligible(props.currentUserId, [
                            { key: "schedule_management_global_category_search", element: "#schedule-category-search" },
                        ]);
                    });
                }
            });
    });

    // Expose methods for parent component
    defineExpose({
        fetchSingleMatSchedule,
        fetchMatSchedules,
    });
</script>

<style scoped></style>
